import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmSignUp } from 'aws-amplify/auth';
import ErrorToast from '../../components/ErrorToast';
import SuccessToast from '../../components/SuccessToast';
import { CheckCircleIcon, WarningTriangleIcon } from '../../assets/icons';
import hansepayWhiteLogo from '../../assets/hansepay-white.png';

function ConfirmUser() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const userName = searchParams.get('userName');
  const code = searchParams.get('code');

  const [result, setResult] = useState();
  const [error, setError] = useState();
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    if (userName && code) {
      confirmSignUp({ username: userName, confirmationCode: code })
        .then(() => {
          setVerifying(false);
          setResult('SUCCESS');
        })
        .catch((err) => {
          setVerifying(false);
          setError({ detail: err.message || err.name || 'Verification failed' });
        });
    } else {
      setVerifying(false);
      setError({ detail: 'Verification parameters missing' });
    }
  }, [userName, code]);

  useEffect(() => {
    if (result) {
      const state = userName && userName.includes('@') ? { state: { email: userName } } : {};
      const timer = setTimeout(() => navigate('/login', state), 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [result, navigate, userName]);

  const handleManualRedirect = () => {
    const state = userName && userName.includes('@') ? { state: { email: userName } } : {};
    navigate('/login', state);
  };

  return (
    <div className='min-h-screen bg-[#EBF4FB] text-[#0B1929]'>
      <header className='flex h-[60px] items-center bg-[linear-gradient(135deg,#060D1A_0%,#0F2540_55%,#163659_100%)] px-5 sm:px-10'>
        <a href='/' className='flex items-center gap-2.5 no-underline'>
          <img src={hansepayWhiteLogo} width={26} height={26} alt='HansePay' className='block shrink-0' />
          <span className='wordmark inline-block translate-y-px text-[18px] leading-none tracking-[-0.015em] text-white'>HansePay</span>
        </a>
      </header>

      <main className='flex justify-center px-4 pb-16 pt-[10vh] sm:px-6'>
        <div className='w-full max-w-[540px]'>
        <ErrorToast error={error} onDismiss={() => setError(null)} autoHide={false} />

        <SuccessToast message={result ? 'Email confirmed successfully!' : null} onDismiss={() => setResult(null)} autoHide={false} />

        <div className='space-y-4'>
          <div className='text-center'>
            <h2 className='font-serif text-[28px] font-normal leading-tight text-[#0B1929]'>Email Verification</h2>
            <p className='mt-1 text-sm text-[#7A9AB0]'>Confirming your email address</p>
          </div>

          <div className='overflow-hidden rounded-[20px] bg-white shadow-[0_4px_32px_rgba(11,25,41,0.08)]'>
            <div className='px-6 py-10 sm:px-12 sm:py-12'>
              {verifying && (
                <div className='flex flex-col items-center justify-center py-5 text-center'>
                  <div className='mb-5 h-12 w-12 animate-spin rounded-full border-[3px] border-[#C4DDEF] border-t-[#1E4E80]' />
                  <h3 className='font-serif text-2xl font-normal leading-tight text-[#0B1929]'>Checking your link.</h3>
                  <p className='mt-2 text-sm leading-6 text-[#7A9AB0]'>Verifying your email...</p>
                </div>
              )}

              {!verifying && result && (
                <div className='flex flex-col items-center justify-center py-5 text-center'>
                  <div className='mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#DCFCE7] text-[#16A34A] ring-8 ring-[#F0FDF4]'>
                    <CheckCircleIcon className='h-10 w-10 text-[#16A34A]' />
                  </div>
                  <div className='space-y-2'>
                    <h3 className='font-serif text-2xl font-normal leading-tight text-[#0B1929]'>Email Confirmed!</h3>
                    <p className='mx-auto max-w-sm text-sm leading-6 text-[#7A9AB0]'>
                      Your email has been successfully verified. You will be redirected to the login page shortly.
                    </p>
                  </div>
                  <button
                    type='button'
                    onClick={handleManualRedirect}
                    className='mt-7 inline-flex h-12 min-w-[180px] items-center justify-center rounded-full bg-[#0B1929] px-7 text-sm font-semibold text-white transition hover:bg-[#0F2540]'
                  >
                    Go to Login
                  </button>
                </div>
              )}

              {!verifying && error && (
                <div className='flex flex-col items-center justify-center py-5 text-center'>
                  <div className='mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[#FEF2F2] text-[#C0392B] ring-8 ring-[#FFF7F7]'>
                    <WarningTriangleIcon className='h-9 w-9' />
                  </div>
                  <div className='space-y-2'>
                    <h3 className='font-serif text-2xl font-normal leading-tight text-[#0B1929]'>Verification Failed</h3>
                    <p className='mx-auto max-w-sm text-sm leading-6 text-[#7A9AB0]'>
                      We couldn&apos;t verify your email address. Please check your verification link or contact support.
                    </p>
                  </div>
                  <div className='mt-7 flex w-full flex-col gap-3 sm:flex-row'>
                    <button
                      type='button'
                      onClick={handleManualRedirect}
                      className='inline-flex h-12 flex-1 items-center justify-center rounded-full border-[1.5px] border-[#0B1929]/15 bg-transparent px-7 text-sm font-semibold text-[#3D5A73] transition hover:bg-[#EBF4FB]'
                    >
                      Go to Login
                    </button>
                    <button
                      type='button'
                      onClick={() => navigate('/register')}
                      className='inline-flex h-12 flex-1 items-center justify-center rounded-full bg-[#0B1929] px-7 text-sm font-semibold text-white transition hover:bg-[#0F2540]'
                    >
                      Register Again
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </main>
    </div>
  );
}

export default ConfirmUser;
